/**
 * Picker: locked state, the lock screen for a device shared by the home —
 * whatever that device is. ("shared-iPad lock screen" until T765: Alex ruled
 * 2026-08-04 that the home chooses the device, so this screen has to work on a
 * phone, a home-issued tablet or a reception desktop alike.) Renders zero
 * resident/request data (spec constraint): only nurse names and the home
 * name. Tap a name -> PIN pad -> POST /api/auth/pin-login.
 *
 * ⛔ RECOVERY HERE IS UNCONDITIONAL, AND THAT IS THE DESIGN — board 1050.
 * This screen used to route on a `409 pin_reset` to send a de-credentialed
 * nurse to Enrol. Board 786/1049 unified pin-login onto one response, so there
 * is now NO signal that distinguishes "she must re-enrol" from "wrong PIN":
 * the bodies are byte-identical, timing is floored, and `hasPin` was dropped
 * from the locked roster precisely because serving it WAS the oracle. The
 * routing cannot be restored without re-opening 786.
 *
 * So instead of detecting who needs the email route, this screen offers it to
 * everyone, always, on the pad itself. That matters for more than tidiness: a
 * nurse who is never told what to do simply RETRIES, and retries are what trip
 * the per-device walk cap (board 787). Measured: a nurse joins the walked set
 * on her SECOND failure, and the cap denies the whole device at three such
 * nurses — so three nurses retrying twice lock the tablet for everyone, a
 * fourth nurse with the CORRECT PIN included, for up to 15 minutes. Giving her
 * somewhere to go is what stops the storm at source.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Card, FormField } from '@ecmb/ui';
import type { ApiClient } from '../api.js';
import type { RosterEntry } from '../state.js';
import { pickerCopy, enrolCopy, sharedCopy } from '../copy.js';

/**
 * ⭐ F-IMP-J2 (T1723 M4) — what the lock screen says when nobody is enrolled on
 * this device yet. See the render branch below for why it is local rather than
 * in `client/copy.ts`.
 *
 * ⚠️ WORDING RULES IT FOLLOWS: it states the situation without blaming anyone or
 * naming a fault, it uses "yet" because enrolling is something somebody does
 * next rather than a limitation, and it carries no dash (the copy gate).
 */
export const PICKER_EMPTY_ROSTER = 'No one is enrolled on this device yet.';

/**
 * ⭐ T2173 — the sentence under the email route on the PIN pad, naming the case a
 * nurse cannot be told directly (Alex, 2026-09-18, from Vicky's reset).
 *
 * ⛔ LOCAL, LIKE `PICKER_EMPTY_ROSTER` ABOVE, AND FOR A MECHANICAL REASON:
 * `check-copy.sh` keys four clause exemptions into `client/copy.ts` BY LINE
 * NUMBER, and `pickerCopy` sits above all four, so one new key there moves every
 * one of them and reds `check:copy` with a FAIL plus a stale-entry WARN. The
 * repo's own answer is either a new object at the END of that file (what
 * `homeRequestsCopy` and `staffCopy` did) or screen-local copy (what the
 * constant above did). One string for one screen takes the second.
 *
 * ⚠️ WORDING RULES IT FOLLOWS: it is CONDITIONAL ("if"), because the client
 * cannot know whether this nurse's PIN was reset and must not appear to; it
 * promises only what the recovery path actually delivers (`enrolCopy`'s own note
 * carries the same promise, and `pin-set` allows it only for a pin-pending
 * session); and it carries no dash (the copy gate).
 */
export const PICKER_EMAIL_HINT = 'If your PIN was reset, sign in with your work email to set a new one.';

/** The hint's id, so the promoted button POINTS at it. Both hints the sibling
 *  Staff screen added in this same change are `aria-describedby`-linked; this one
 *  was a bare paragraph, so a screen-reader user heard "Sign in with email" with
 *  no trace of the condition the old, longer label used to carry. */
export const PICKER_EMAIL_HINT_ID = 'picker-email-hint';

export type PickerProps = {
  api: ApiClient;
  roster: RosterEntry[];
  homeName: string | null;
  /** Fires on a successful pin-login (200); caller re-fetches auth state. */
  onActive: () => void;
  /** Fires when the nurse asks to sign in with email instead — from the roster
   *  ("different nurse", no note) or from the PIN pad ("trouble with your PIN",
   *  carrying `enrolCopy.pinTroubleNotice`). `note` (if given) is shown on the
   *  Enrol screen. ⚠️ It is never fired FROM A RESPONSE any more: the old
   *  "a pin-login response says the PIN was reset" trigger died with board
   *  786/1049's uniform 401. */
  onNeedsEnrol: (note?: string) => void;
  /** ⭐ Board 2706 rework (R3) — a New request is held for whoever signs back in: say so, on both
   *  views, as a plain line (it is a standing fact, not an event, so no live region). */
  requestKept?: boolean;
};

export function Picker({ api, roster, homeName, onActive, onNeedsEnrol, requestKept = false }: PickerProps) {
  const keptLine = requestKept ? <p>{pickerCopy.requestKept}</p> : null;
  const [selected, setSelected] = useState<RosterEntry | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pinRef = useRef<HTMLInputElement>(null);

  /**
   * ⭐ T1749 M9 (DS-27) — THE PAD'S `autoFocus` IS NOW AN EFFECT, because
   * `FormField` has no `autoFocus` prop and inventing one would have put a
   * mount-time side effect into a shared component for a single screen.
   * ⛔ KEYED ON `selected`, NOT ON EVERY RENDER. The pad mounts when a nurse is
   * tapped, so that is the moment the caret belongs in the PIN box; re-focusing
   * on every render would also fight `refusePin`'s own focus call and would break
   * `picker.spec.tsx`'s non-vacuity check, which deliberately parks focus on the
   * Unlock button before submitting so the refusal has something to move.
   */
  useEffect(() => {
    if (selected) pinRef.current?.focus();
  }, [selected]);

  /**
   * ⭐ T1749 M9 (DS-27) — `PIN_ERROR_ID` IS GONE, not orphaned beside a component
   * that ignores it. `FormField` derives the message's id from the control's own
   * (`errorIdFor`), and derives the identical string this screen hand-wrote:
   * `picker-pin` -> `picker-pin-error`. The rule the constant enforced by
   * convention is now enforced by construction.
   */

  /**
   * ⭐⭐ II-portal-#21 · ADR4-D10(b) (T1723 M4) — A REFUSED PIN MOVES THE NURSE
   * TO THE PROBLEM, AND SAYS SO IN THE MARKUP.
   *
   * ⛔ WHAT WAS WRONG, measured live at M2: submitting a wrong PIN left focus on
   * BODY (she pressed a button, so focus was on the button; the re-render moved
   * nothing), the input carried neither `aria-invalid` nor `aria-describedby`,
   * and its border stayed `--line`. That last one is why 1717's
   * `[aria-invalid='true']` border rule could not reach this screen: nothing
   * here ever SET the attribute. So a screen-reader user was told nothing, and a
   * sighted nurse had a message with no marked control.
   *
   * ⛔ `focus()`, NEVER `scrollIntoView()` — the house shape from
   * `RequestForm.tsx`'s `focusFirstError`. Focus scrolls the control into view
   * for free AND announces the field with its new invalid state; a scroll moves
   * the page and tells assistive technology nothing.
   * ⛔ ONE FUNCTION FOR EVERY REFUSAL, so no branch can be added later that
   * marks the field and forgets to move to it. `clearPin` stays a per-branch
   * decision: a 429 is the device cap, enforced BEFORE the PIN is checked, so
   * the typed PIN may be perfectly good and clearing it makes her retype it.
   *
   * ⛔⛔ AND SINCE T1749 M9 (DS-27) THE `focus()` BELOW IS LOAD-BEARING FOR THE
   * ANNOUNCEMENT ITSELF, not only for where the caret lands. The message moved
   * into `FormField`'s `error` slot, which carries NO `role="alert"` by design
   * (`FormField.tsx`: a per-field message is not an interruption) and is
   * announced through `aria-describedby` — which a screen reader reads only when
   * something LANDS on the control. Deleting this line would make every refusal
   * on this screen silent rather than merely badly placed.
   */
  function refusePin(message: string, options: { clearPin: boolean }): void {
    setError(message);
    if (options.clearPin) setPin('');
    pinRef.current?.focus();
  }

  async function submitPin(): Promise<void> {
    if (!selected || busy) return;
    setBusy(true);
    setError(null);
    const res = await api.post('/api/auth/pin-login', { nurseId: selected.id, pin });
    setBusy(false);

    if (res.kind === 'network-error') {
      refusePin(sharedCopy.networkError, { clearPin: false });
      return;
    }
    if (res.status === 200) {
      onActive();
      return;
    }
    /**
     * ⛔ THE PIN IS NOT CLEARED HERE, AND THIS MUST NOT FALL THROUGH.
     * A 429 is the per-device walk cap (board 787), which is enforced BEFORE
     * `verifyPin` — so the typed PIN was never checked and is very possibly
     * correct. Clearing it would make the nurse retype a good PIN, and the
     * generic error would tell her nothing actionable, which is what produces
     * the retrying that trips the cap in the first place. Same defect class
     * Enrol closed for code-verify, where a 429 told a nurse holding a valid
     * code that her code was wrong and then cleared it.
     */
    if (res.status === 429) {
      refusePin(pickerCopy.deviceCappedError, { clearPin: false });
      return;
    }
    /**
     * ⛔ STATUS ALONE — never `res.body`. Board 786 collapsed wrong-PIN, no-PIN
     * and PIN-just-destroyed onto one identical 401 body; inspecting the body to
     * tell them apart is the oracle that closed, rebuilt on the client. Every
     * 401 therefore renders exactly one string.
     *
     * ⚠️ Accepted cost: pin-login also returns `401 no_device`, which renders as
     * "that PIN did not work". Untrue for that case, but near-unreachable (this
     * screen renders only from `state: 'locked'`, which requires a live device)
     * and the remedy the copy offers is the right one anyway. Do NOT special-case
     * it by reading the body.
     */
    refusePin(res.status === 401 ? pickerCopy.pinFailedError : sharedCopy.genericError, {
      clearPin: true,
    });
  }

  function onSubmit(e: JSX.TargetedEvent<HTMLFormElement, Event>): void {
    e.preventDefault();
    void submitPin();
  }

  if (selected) {
    return (
      <div class="screen picker-screen">
        <h1>{pickerCopy.pinPromptTitle}</h1>
        <p class="picker-selected-name">{selected.displayName}</p>
        {keptLine}
        {/* ⭐ `shape="cover"` MATCHES THE KIOSK SIGN-IN (`quote-helper/src/client/screens/Login.tsx`).
            The two tools' sign-in screens are the pair a reviewer puts side by side and asks whether
            they read as one system, and a different card padding is exactly the difference she would
            have to name. Ruled at T1749 M9 (DS-27); `card` (the default) is what the first pass left. */}
        <Card shape="cover">
          <form onSubmit={onSubmit}>
            {/* ⛔ `pattern` SURVIVED THE MOVE ONTO `FormField`, and that is why
                the component gained the prop at T1749 M9 instead of the field
                losing it. A non-numeric PIN is refused by the browser BEFORE the
                POST, so it never spends one of this device's walk-cap attempts —
                the cap this file's header measures at three nurses failing twice
                each locking the tablet for everyone, correct PIN included. */}
            <FormField
              id="picker-pin"
              label={pickerCopy.pinLabel}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              /* PINs are 6-10 digits; a 6 cap here silently truncated longer
                 PINs so Unlock could never succeed (round-3, P2). */
              maxLength={10}
              value={pin}
              onInput={setPin}
              error={error ?? undefined}
              elementRef={pinRef}
            />
            <button type="submit" class="btn btn--primary" disabled={busy}>
              {pickerCopy.unlockButton}
            </button>
          </form>
        </Card>
        <button
          type="button"
          class="btn btn--link"
          onClick={() => {
            setSelected(null);
            setPin('');
            setError(null);
          }}
        >
          {pickerCopy.pinBackLink}
        </button>
        {/*
          ⛔ ALWAYS RENDERED, never gated on a failure or on any response — the
          client cannot know who needs it (board 786), and a nurse who already
          knows her PIN was cleared should not have to fail once to find the
          door. Being unconditional is also what keeps it disclosure-free: it
          carries zero bits about her PIN state.

          ⭐ T2173 — THE AFFORDANCE CHANGED, THE DISCLOSURE DID NOT. Vicky's PIN
          was reset by an admin; she typed her old PIN, got the one unified
          failure string, and retried instead of taking the email route, because
          the route read as a footer link. So after a refusal the SAME always
          rendered control is painted as a real button (`btn--quiet`, NOT
          `btn--secondary`. DESIGN.md §5.1 rules exactly ONE visible primary per
          screen; at `secondary` this control's navy fill MEASURED wider than the
          gold `Unlock` primary, because `Unlock` sits inside the `Card` and this
          does not, so the recovery route out-painted the thing it is an
          alternative to. The figures are a seat measurement, not a DESIGN.md
          constant — re-derive at 1024 with:
          `['Unlock','Sign in with email'].map(n => [...document.querySelectorAll('button')].find(b => b.textContent.trim() === n).getBoundingClientRect()).map(r => Math.round(r.width) + 'x' + Math.round(r.height))`)
          and the
          hint below names the case.
          ⛔ `error !== null` IS NOT A NEW BIT. It is set by a refusal the nurse
          just saw on her own screen, and it is the SAME for a wrong PIN, a
          cleared PIN and a nurse who never had one — `submitPin` renders exactly
          one string for every 401 (the docblock above it). The paint therefore
          carries no more information than the error text already showing, and
          nothing here reads a response body or a status beyond that one branch.
          ⚠️ The hint is STATIC and unconditional: it names a possibility, never
          this nurse's state, which is what keeps it enumeration-safe.
        */}
        <button
          type="button"
          class={error !== null ? 'btn btn--quiet' : 'btn btn--link'}
          aria-describedby={PICKER_EMAIL_HINT_ID}
          onClick={() => onNeedsEnrol(enrolCopy.pinTroubleNotice)}
        >
          {pickerCopy.useEmailLink}
        </button>
        <p class="field-hint" id={PICKER_EMAIL_HINT_ID}>
          {PICKER_EMAIL_HINT}
        </p>
      </div>
    );
  }

  return (
    <div class="screen picker-screen">
      <h1>{homeName ?? pickerCopy.fallbackTitle}</h1>
      {/* ⭐⭐ F-IMP-J2 (T1723 M4) — THE EMPTY LOCK SCREEN DEAD-ENDED. With nobody
          enrolled on the device, this screen rendered its heading, the
          instruction "Tap your name" and then an EMPTY grid: it told a nurse to
          tap something that was not there, and the way out (the email link
          below) read as a footnote to a list rather than as the only thing to
          do.
          ⛔ THE INSTRUCTION IS SWAPPED, NOT SUPPLEMENTED. Leaving "Tap your
          name" on screen beside "no one is enrolled" is two contradictory
          instructions, which is the same defect one line lower down.
          ⚠️ THE STRING IS INLINE, NOT IN `client/copy.ts`: that file is fenced
          for this milestone (M7 owns it), the same fence `Enrol.tsx`'s two
          throttle strings already record. Folding it into `pickerCopy` is the
          named residual.
          ⚠️ NO NEW AFFORDANCE IS INVENTED: the enrol route below is already
          unconditional (board 786's ruling, see this file's header), so the next
          action this branch points at is one that is already on the screen and
          already works. */}
      <p>{roster.length === 0 ? PICKER_EMPTY_ROSTER : pickerCopy.subtitle}</p>
      {keptLine}
      {/* ⛔ THE ROSTER IS DELIBERATELY NOT RE-CUT ONTO `Card` AT T1749 M9, AND
          THE REASON IS THE SAME ONE THAT STOPPED THE SHELF COVER. Each
          `.picker-nurse-btn` below is an INTERACTIVE card — a `<button>` wearing
          the card look (radius, hairline, shadow: `portal/src/client/styles/app.css`)
          — and `Card` renders a `<div>`. Wrapping the button in one would nest a
          container inside a control; replacing the button with one would cost the
          screen its only affordance, on a lock screen whose whole instruction is
          "tap your name". M9's business on this screen is its GRAMMAR (the PIN pad
          onto `Card` + `FormField`), so the shape question goes with the shelf
          cover's to M10, which owns the interactive-card question for both tools.
          Recorded here rather than only in the plan, because this is where the
          next reader asks why one half of the screen adopted and the other did
          not. */}
      {roster.length > 0 && (
        <div class="picker-roster">
          {roster.map((nurse) => (
            <button
              key={nurse.id}
              type="button"
              class="picker-nurse-btn"
              onClick={() => {
                setSelected(nurse);
                setPin('');
                setError(null);
              }}
            >
              {nurse.displayName}
            </button>
          ))}
        </div>
      )}
      <button type="button" class="btn btn--link" onClick={() => onNeedsEnrol()}>
        {pickerCopy.differentNurseLink}
      </button>
    </div>
  );
}
